import {
    useNavigate,
    useParams,
    useBlocker,
    BlockerFunction,
} from 'react-router-dom';
import useExperienceBuilder from './hooks/useExperienceBuilder';
import {
    XCircleIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';
import useExperienceDefinition from './hooks/useExperienceDefinition';
import { useCallback, useEffect, useState } from 'react';
import ExperienceEditor from './ExperienceEditor';
import VisualExperienceBuilder from './VisualExperienceBuilder';
import {
    createScreen,
    deleteScreen,
    deleteUIElement,
    resetScreens,
} from './utils/state/screensSlice';
import { addScreenToExperience } from './utils/state/experienceSlice';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogBackdrop,
} from '@headlessui/react';
import { toast } from 'sonner';
import { ChevronRightIcon, LibraryIcon, TrashIcon } from 'lucide-react';
import moment from 'moment';
import Loading from '../Common/components/Loading';
import { resetExperience } from './utils/state/experienceSlice';
import { clearAllFlows } from './utils/state/processingFlowSlice';
import Library from '../Processing/Library/components/Library';
import { Provider, useSelector, useDispatch } from 'react-redux';
import createExperienceStore from './utils/experienceStore';
import {
    toggleFullscreen,
    selectScreen,
    selectElement,
} from './utils/state/canvasSlice';
import ExperienceErrorBoundary from './ExperienceErrorBoundary';
import SavePromptDialog from './SavePromptDialog';
import { UndoRedoKeyboardHandler } from './UndoRedoToolbar';
import HistoryPanel from './HistoryPanel';
import { setupUndoRedoSync } from './utils/undoRedo/undoRedoSlice';

// Create the store instance
const { store } = createExperienceStore();

function ExperienceBuilderContent() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { activationId } = useParams();
    const [submitting, setSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState(new Date());
    const [isLoading, setIsLoading] = useState(true);
    const {
        definition,
        screenCount,
        processingFlowCount,
        warnings,
        errors,
        importDefinition,
        exportDefinition,
        screens,
        allScreens,
        sharing,
        hasUnsavedChanges,
        setLastSavedDefinition,
        createSharingStation,
        deleteSharingStation,
        addScreenToSharingStation,
        deleteScreenFromSharingStation,
        updateSharingStationData,
    } = useExperienceBuilder(activationId ? +activationId : 0);

    // Use Redux canvas state for all screen/element selection
    const { selectedScreenId, selectedElementId } = useSelector(
        (state: any) => state.canvas,
    );

    const currentScreen: ScreenDefinition | null = useSelector((state: any) => {
        if (selectedScreenId) {
            return state.screens.present.entities[selectedScreenId];
        }
        return null;
    });

    // Get selected element from current screen
    const selectedElement =
        selectedElementId && currentScreen
            ? currentScreen.elements.find(
                  (el: UIElement) => el.id === selectedElementId,
              )
            : null;

    const { getDefinition, saveDefinition, deleteDefinition } =
        useExperienceDefinition();

    useEffect(() => {
        if (definition === null) {
            getDefinition(activationId ? +activationId : 0)
                .then((experience) => {
                    if (experience) {
                        importDefinition(
                            JSON.stringify(experience.definition),
                            activationId ? +activationId : 0,
                        );
                        setLastSaved(new Date());
                        setLastSavedDefinition(experience.definition);
                    }
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize undo/redo system
    useEffect(() => {
        const unsubscribe = setupUndoRedoSync(dispatch);
        return unsubscribe;
    }, [dispatch]);

    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }: any) => {
            const shouldBlock =
                hasUnsavedChanges &&
                (currentLocation.pathname !== nextLocation.pathname ||
                    currentLocation.hash !== nextLocation.hash);

            return shouldBlock;
        },
        [hasUnsavedChanges],
    );

    const blocker = useBlocker(shouldBlock);

    const [deleteExperienceDialogOpen, setDeleteExperienceDialogOpen] =
        useState(false);
    const [builderMode, setBuilderMode] = useState<BuilderMode>('visual');
    const [isInVisualEditor, setIsInVisualEditor] = useState(false);
    const { isFullscreen, isFlowEditorOpen } = useSelector(
        (state: any) => state.canvas,
    );

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Window fullscreen functionality (no browser fullscreen)
    const toggleWindowFullscreen = useCallback(() => {
        dispatch(toggleFullscreen());
        // If exiting fullscreen while in visual editor, return to welcome screen
        if (isFullscreen && isInVisualEditor) {
            setIsInVisualEditor(false);
        }
    }, [dispatch, isFullscreen, isInVisualEditor]);

    // Auto-enter fullscreen when entering visual editor
    useEffect(() => {
        if (isInVisualEditor && !isFullscreen) {
            dispatch(toggleFullscreen());
        }
    }, [isInVisualEditor, isFullscreen, dispatch]);

    const saveExperience = useCallback(async () => {
        if (submitting) {
            return;
        }

        setSubmitting(true);

        await saveDefinition(
            activationId ? +activationId : 0,
            exportDefinition(),
        );

        setSubmitting(false);
        setLastSaved(new Date());
        setLastSavedDefinition(definition);
    }, [
        submitting,
        activationId,
        exportDefinition,
        saveDefinition,
        definition,
        setLastSavedDefinition,
    ]);

    const [showSavePrompt, setShowSavePrompt] = useState(false);
    const [isSavingBeforeExit, setIsSavingBeforeExit] = useState(false);

    const handleExitEditor = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowSavePrompt(true);
        } else {
            // No unsaved changes, exit immediately
            setIsInVisualEditor(false);
            toggleWindowFullscreen();
        }
    }, [hasUnsavedChanges, toggleWindowFullscreen]);

    const handleSaveAndExit = useCallback(async () => {
        setIsSavingBeforeExit(true);
        try {
            await saveExperience();
            setShowSavePrompt(false);
            setIsInVisualEditor(false);
            toggleWindowFullscreen();
        } catch (error) {
            console.error('Failed to save before exit:', error);
            toast.error('Failed to save changes. Please try again.');
        } finally {
            setIsSavingBeforeExit(false);
        }
    }, [saveExperience, toggleWindowFullscreen]);

    const handleExitWithoutSaving = useCallback(() => {
        setShowSavePrompt(false);
        setIsInVisualEditor(false);
        toggleWindowFullscreen();
    }, [toggleWindowFullscreen]);

    const handleCancelExit = useCallback(() => {
        setShowSavePrompt(false);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // F11 for fullscreen toggle
            if (e.key === 'F11') {
                e.preventDefault();
                toggleWindowFullscreen();
            }
            // Escape to exit fullscreen/editor
            if (e.key === 'Escape' && isFullscreen) {
                if (isInVisualEditor) {
                    handleExitEditor();
                } else {
                    toggleWindowFullscreen();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        isFullscreen,
        toggleWindowFullscreen,
        isInVisualEditor,
        handleExitEditor,
    ]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showAssetPicker, setShowAssetPicker] = useState(false);

    const createBlankExperience = () => {
        importDefinition(
            JSON.stringify({
                processingFlows: {
                    '1': [],
                    '2': [],
                },
                screens: [],
                sharing: [],
                version: 1,
                schemaVersion: 1,
            }),
            activationId ? +activationId : 0,
        );
        dispatch(createScreen({ id: 'screen-1' }));
        // Add the initial screen to the experience's screen list
        dispatch(
            addScreenToExperience({
                experienceId: activationId ? +activationId : 0,
                screenId: 'screen-1',
            }),
        );
    };

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    let hasErrors = warnings.length > 0 || errors.length > 0;

    return (
        <div
            className={
                isFullscreen
                    ? 'fixed inset-0 bg-white z-50 overflow-hidden pt-16 '
                    : 'mb-20'
            }
        >
            {hasErrors && !isFullscreen && (
                <div className="absolute top-0 right-0 w-1/8 z-[1000]">
                    {showWarnings && (
                        <div className="relative rounded-md bg-yellow-50 p-4">
                            <div className="flex">
                                <div
                                    className="absolute top-0 right-0"
                                    onClick={() => {
                                        setShowWarnings(false);
                                    }}
                                >
                                    <XMarkIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-yellow-400"
                                    />
                                </div>
                                <div className="flex-shrink-0">
                                    <ExclamationTriangleIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-yellow-400"
                                    />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        {warnings.length} warning
                                        {warnings.length > 1 ? 's' : ''} in the
                                        experience.
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <ul className="list-disc space-y-1 pl-5">
                                            {warnings.map((error) => (
                                                <li key={error}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {showErrors && (
                        <div className="relative rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div
                                    className="absolute top-0 right-0"
                                    onClick={() => {
                                        setShowErrors(false);
                                    }}
                                >
                                    <XMarkIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-red-400"
                                    />
                                </div>
                                <div className="flex-shrink-0">
                                    <XCircleIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-red-400"
                                    />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        {errors.length} error
                                        {errors.length > 1 ? 's' : ''} in the
                                        experience.
                                    </h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <ul className="list-disc space-y-1 pl-5">
                                            {errors.map((error) => (
                                                <li key={error}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="">
                {definition === null || screens.length === 0 ? (
                    <div className="ml-8 max-w-lg mt-10 justify-start">
                        <h2 className="text-base font-semibold leading-6 text-gray-900">
                            Create Your Experience
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Get started by selecting a template or start from an
                            empty experience.
                        </p>
                        <ul className="mt-6 divide-y divide-gray-200 border-b border-t border-gray-200">
                            {[
                                /* {
                                    name: 'Clicker',
                                    description:
                                        'A simple clicker experience with on screen sharing.',
                                    iconColor: 'bg-blue-500',
                                    icon: PlusSquareIcon,
                                    href: '#',
                                },
                                {
                                    name: 'GIF',
                                    description:
                                        'A GIF experience with on screen sharing.',
                                    iconColor: 'bg-green-500',
                                    icon: PlusSquareIcon,
                                    href: '#',
                                },
                                {
                                    name: 'Sequence',
                                    description:
                                        'A sequence experience with on screen sharing and printing.',
                                    iconColor: 'bg-red-500',
                                    icon: PlusSquareIcon,
                                    href: '#',
                                },*/
                            ].map((item: any, itemIdx) => (
                                <li key={itemIdx}>
                                    <div className="group relative flex items-start space-x-3 py-4">
                                        <div className="flex-shrink-0">
                                            <span
                                                className={classNames(
                                                    item.iconColor,
                                                    'inline-flex h-10 w-10 items-center justify-center rounded-lg',
                                                )}
                                            >
                                                <item.icon
                                                    aria-hidden="true"
                                                    className="h-6 w-6 text-white"
                                                />
                                            </span>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-gray-900">
                                                <a href={item.href}>
                                                    <span
                                                        aria-hidden="true"
                                                        className="absolute inset-0"
                                                    />
                                                    {item.name}
                                                </a>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {item.description}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 self-center">
                                            <ChevronRightIcon
                                                aria-hidden="true"
                                                className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                            />
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 flex">
                            <span
                                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer"
                                onClick={createBlankExperience}
                            >
                                Or start from scratch
                                <span aria-hidden="true"> &rarr;</span>
                            </span>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`flex flex-col ${
                            isFullscreen ? 'h-screen' : 'h-full'
                        }`}
                    >
                        {/* Mode Toggle - Hidden in fullscreen */}
                        {!isFullscreen && (
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setBuilderMode('visual')}
                                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            builderMode === 'visual'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        Visual Editor
                                    </button>
                                    <button
                                        onClick={() => setBuilderMode('json')}
                                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            builderMode === 'json'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        JSON Editor
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Editor Content */}
                        <div className="flex-1 overflow-hidden">
                            {builderMode === 'visual' ? (
                                definition ? (
                                    isInVisualEditor ? (
                                        <VisualExperienceBuilder
                                            screens={screens}
                                            allScreens={allScreens}
                                            sharing={sharing || []}
                                            activationId={
                                                activationId ? +activationId : 0
                                            }
                                            onExitEditor={() => {
                                                handleExitEditor();
                                            }}
                                            createSharingStation={
                                                createSharingStation
                                            }
                                            deleteSharingStation={
                                                deleteSharingStation
                                            }
                                            addScreenToSharingStation={
                                                addScreenToSharingStation
                                            }
                                            deleteScreenFromSharingStation={
                                                deleteScreenFromSharingStation
                                            }
                                            updateSharingStation={
                                                updateSharingStationData
                                            }
                                            onExport={() => {
                                                const fileName =
                                                    'experience-definition';
                                                const json = exportDefinition();
                                                const blob = new Blob([json], {
                                                    type: 'application/json',
                                                });
                                                const href =
                                                    URL.createObjectURL(blob);

                                                const link =
                                                    document.createElement('a');
                                                link.href = href;
                                                link.download =
                                                    fileName + '.json';
                                                document.body.appendChild(link);
                                                link.click();

                                                // clean up "a" element & remove ObjectURL
                                                document.body.removeChild(link);
                                                URL.revokeObjectURL(href);
                                                toast.success(
                                                    'Experience definition exported.',
                                                );
                                            }}
                                            onImport={() => {
                                                const input =
                                                    document.createElement(
                                                        'input',
                                                    );
                                                input.type = 'file';
                                                input.accept = '.json';
                                                input.onchange = (e) => {
                                                    const file = (
                                                        e.target as HTMLInputElement
                                                    ).files
                                                        ? (
                                                              e.target as HTMLInputElement
                                                          ).files![0]
                                                        : null;
                                                    if (file) {
                                                        const reader =
                                                            new FileReader();
                                                        reader.onload = (e) => {
                                                            const text =
                                                                e.target
                                                                    ?.result;
                                                            if (
                                                                typeof text ===
                                                                'string'
                                                            ) {
                                                                importDefinition(
                                                                    text,
                                                                    activationId
                                                                        ? +activationId
                                                                        : 0,
                                                                );
                                                                saveExperience();
                                                                toast.success(
                                                                    'Experience definition imported.',
                                                                );
                                                            }
                                                        };
                                                        reader.readAsText(file);
                                                    }
                                                };
                                                input.click();
                                            }}
                                            onSave={saveExperience}
                                            hasUnsavedChanges={
                                                hasUnsavedChanges
                                            }
                                            lastSaved={lastSaved}
                                            submitting={submitting}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-indigo-100">
                                            <div className="text-center p-12 max-w-2xl">
                                                <div className="mb-8">
                                                    <div className="mx-auto w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center mb-6">
                                                        <svg
                                                            className="w-12 h-12 text-white"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                                        Welcome to the Visual
                                                        Experience Builder
                                                    </h1>
                                                    <p className="text-xl text-gray-600 mb-8">
                                                        Design amazing photo
                                                        booth experiences with
                                                        our intuitive visual
                                                        editor. Drag, drop, and
                                                        connect screens to
                                                        create engaging user
                                                        journeys.
                                                    </p>
                                                </div>
                                                <div className="space-y-4">
                                                    <button
                                                        onClick={() =>
                                                            setIsInVisualEditor(
                                                                true,
                                                            )
                                                        }
                                                        className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform"
                                                    >
                                                        <svg
                                                            className="w-6 h-6 mr-3"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M13 10V3L4 14h7v7l9-11h-7z"
                                                            />
                                                        </svg>
                                                        Enter Visual Editor
                                                    </button>
                                                    <p className="text-sm text-gray-500">
                                                        The editor will open in
                                                        full window mode for the
                                                        best experience
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center p-8">
                                            <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                                Loading Visual Builder...
                                            </h2>
                                            <p className="text-gray-600">
                                                Initializing experience
                                                definition
                                            </p>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <ExperienceEditor
                                    screens={screens}
                                    definition={definition}
                                    warnings={warnings}
                                    errors={errors}
                                    activationId={
                                        activationId ? +activationId : 0
                                    }
                                    onScreenSelected={(screenId) => {
                                        console.log(
                                            'Screen selected',
                                            screenId,
                                        );
                                        // Update Redux state only
                                        dispatch(selectScreen(screenId));
                                    }}
                                    onDefinitionChange={(definition) => {
                                        console.log(
                                            'Definition changed',
                                            definition,
                                        );
                                        importDefinition(
                                            JSON.stringify(definition),
                                            activationId ? +activationId : 0,
                                        );
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div
                className={`fixed border border-gray-300 border-solid ${
                    isFullscreen ? 'bottom-[154px]' : 'bottom-[154px]'
                } left-2 bg-white rounded-lg flex-col p-2 gap-3 grid z-[200]`}
            >
                {warnings.length > 0 && (
                    <div
                        className="text-sm  relative"
                        onClick={() => {
                            setShowWarnings(!showWarnings);
                        }}
                    >
                        <ExclamationTriangleIcon
                            aria-hidden="true"
                            className="h-6 w-6 text-yellow-400"
                        />
                        <div className="text-xs font-semibold  absolute top-[-7px] right-[-7px]">
                            <div className="bg-yellow-400 text-white rounded-full w-4 h-4 flex items-center justify-center">
                                {warnings.length}
                            </div>
                        </div>
                    </div>
                )}

                {errors.length > 0 && (
                    <div
                        className="text-sm relative"
                        onClick={() => {
                            setShowErrors(!showErrors);
                        }}
                    >
                        <XCircleIcon
                            aria-hidden="true"
                            className="h-6 w-6 text-red-400"
                        />
                        <div className="text-xs font-semibold  absolute top-[-7px] right-[-7px]">
                            <div className="bg-red-400 text-white rounded-full w-4 h-4 flex items-center justify-center">
                                {errors.length}
                            </div>
                        </div>
                    </div>
                )}
                {/* <button
                    className={`flex items-center gap-2 cursor-pointer`}
                    onClick={() => {
                        const newScreen = {
                            id: `screen-${Math.random()
                                .toString(36)
                                .substring(2, 8)}`,
                            elements: [],
                        };
                        dispatch(createScreen(newScreen));
                        // Add the screen to the experience's screen list
                        dispatch(addScreenToExperience({
                            experienceId: activationId ? +activationId : 0,
                            screenId: newScreen.id,
                        }));
                        dispatch(selectScreen(newScreen.id));
                    }}
                    title="Add Screen"
                >
                    <PlusSquareIcon />
                </button>
                */}
                <button
                    className="h-6 w-6 hover:text-blue-500 cursor-pointer"
                    onClick={() => setShowAssetPicker(true)}
                >
                    <LibraryIcon />
                </button>
                <button
                    className="h-6 w-6 hover:text-red-500 cursor-pointer"
                    onClick={() => {
                        setDeleteExperienceDialogOpen(true);
                    }}
                    title="Delete Experience"
                >
                    <TrashIcon />
                </button>
            </div>
            <div className="">
                <div
                    className={`mt-6 flex items-center justify-end gap-x-6 px-6 fixed bottom-0 left-0 right-0 z-[300] border-t border-gray-900/10 bg-white p-3 ${
                        isFlowEditorOpen || isInVisualEditor ? 'hidden' : ''
                    }`}
                >
                    <button
                        className="rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black  disabled:opacity-50"
                        onClick={() => {
                            const fileName = 'experience-definition';
                            const json = exportDefinition();
                            const blob = new Blob([json], {
                                type: 'application/json',
                            });
                            const href = URL.createObjectURL(blob);

                            const link = document.createElement('a');
                            link.href = href;
                            link.download = fileName + '.json';
                            document.body.appendChild(link);
                            link.click();

                            // clean up "a" element & remove ObjectURL
                            document.body.removeChild(link);
                            URL.revokeObjectURL(href);
                            toast.success('Experience definition exported.');
                        }}
                        disabled={!definition}
                    >
                        Export
                    </button>
                    <button
                        className="rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.json';
                            input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement)
                                    .files
                                    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      //@ts-ignore
                                      (e.target as HTMLInputElement).files[0]
                                    : null;
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (e) => {
                                        const text = e.target?.result;
                                        if (typeof text === 'string') {
                                            importDefinition(
                                                text,
                                                activationId
                                                    ? +activationId
                                                    : 0,
                                            );
                                            saveExperience();

                                            toast.success(
                                                'Experience definition imported.',
                                            );
                                        }
                                    };
                                    reader.readAsText(file);
                                }
                            };
                            input.click();
                        }}
                    >
                        Import
                    </button>

                    <div className="spacer" />
                    {definition && (
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex flex-row gap-2">
                                <div className="Form-row">
                                    <label
                                        htmlFor="screenCount"
                                        className="text-sm font-semibold text-gray-900"
                                    >
                                        Screens:
                                    </label>
                                    <span className="text-sm font-semibold text-gray-900">
                                        {screenCount}
                                    </span>
                                </div>
                                <div className="Form-row">
                                    <label
                                        htmlFor="processingFlowCount"
                                        className="text-sm font-semibold text-gray-900"
                                    >
                                        Flows:
                                    </label>
                                    <span className="text-sm font-semibold text-gray-900">
                                        {processingFlowCount}
                                    </span>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500">
                                Last saved {moment(lastSaved).fromNow()}
                            </div>
                        </div>
                    )}
                    <div className="spacer" />

                    <button
                        className="text-sm font-semibold leading-6 text-gray-900"
                        type="button"
                        onClick={() => navigate(-1)}
                    >
                        <span>Cancel</span>
                    </button>
                    <button
                        className="rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:opacity-50 transition-all"
                        type="submit"
                        onClick={saveExperience}
                        disabled={!hasUnsavedChanges}
                    >
                        Save
                    </button>
                </div>
            </div>

            <Dialog
                open={deleteExperienceDialogOpen}
                onClose={() => setDeleteExperienceDialogOpen(false)}
                className="relative z-20"
            >
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ExclamationTriangleIcon
                                            aria-hidden="true"
                                            className="h-6 w-6 text-red-600"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <DialogTitle
                                            as="h3"
                                            className="text-base font-semibold leading-6 text-gray-900"
                                        >
                                            Delete Entire Experience
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete
                                                the entire experience? This
                                                action cannot be undone and will
                                                remove all screens and elements.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                    onClick={async () => {
                                        if (activationId) {
                                            await deleteDefinition(
                                                activationId
                                                    ? +activationId
                                                    : 0,
                                            );
                                        }
                                        dispatch(resetScreens());
                                        dispatch(clearAllFlows());
                                        dispatch(resetExperience());

                                        setDeleteExperienceDialogOpen(false);
                                    }}
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    onClick={() =>
                                        setDeleteExperienceDialogOpen(false)
                                    }
                                >
                                    Cancel
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
            <Dialog
                open={deleteDialogOpen}
                onClose={setDeleteDialogOpen}
                className="relative z-20"
            >
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ExclamationTriangleIcon
                                            aria-hidden="true"
                                            className="h-6 w-6 text-red-600"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <DialogTitle
                                            as="h3"
                                            className="text-base font-semibold leading-6 text-gray-900"
                                        >
                                            Delete{' '}
                                            {selectedElement
                                                ? 'Element'
                                                : 'Screen'}
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete
                                                this{' '}
                                                {selectedElement
                                                    ? 'element'
                                                    : 'screen'}
                                                ? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDeleteDialogOpen(false);
                                        if (selectedElement) {
                                            dispatch(
                                                deleteUIElement({
                                                    elementId:
                                                        selectedElement.id,
                                                    screenId: currentScreen?.id,
                                                }),
                                            );
                                            dispatch(selectElement(undefined));
                                        } else if (currentScreen) {
                                            dispatch(
                                                deleteScreen({
                                                    screenId: currentScreen.id,
                                                }),
                                            );
                                            dispatch(selectScreen(undefined));
                                        }
                                        toast.success(
                                            `Deleted ${
                                                selectedElement
                                                    ? 'element'
                                                    : 'screen'
                                            }`,
                                        );
                                    }}
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    data-autofocus
                                    onClick={() => setDeleteDialogOpen(false)}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={blocker?.state === 'blocked'}
                onClose={() => blocker?.reset?.()}
                className="relative z-20"
            >
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ExclamationTriangleIcon
                                            aria-hidden="true"
                                            className="h-6 w-6 text-red-600"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <DialogTitle
                                            as="h3"
                                            className="text-base font-semibold leading-6 text-gray-900"
                                        >
                                            Discard unsaved changes?
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                You have unsaved changes.
                                                <br />
                                                <br />
                                                Are you sure you want to discard
                                                them?
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        blocker?.proceed?.();
                                    }}
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                >
                                    Yes, discard changes
                                </button>
                                <button
                                    type="button"
                                    data-autofocus
                                    onClick={() => {
                                        blocker?.reset?.();
                                    }}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            {showAssetPicker && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center z-50"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowAssetPicker(false);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            navigator.clipboard.writeText(value);

                            setShowAssetPicker(false);
                            toast.success('Asset URL Copied to clipboard');
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowAssetPicker(false);
                        }}
                    />
                </div>
            )}

            {/* Save Prompt Dialog */}
            {/* Undo/Redo Keyboard Handler */}
            <UndoRedoKeyboardHandler />

            {/* History Panel */}
            <HistoryPanel className="fixed top-16 right-0 w-80 h-[calc(100vh-4rem)] z-40" />

            <SavePromptDialog
                isOpen={showSavePrompt}
                onClose={handleCancelExit}
                onSaveAndExit={handleSaveAndExit}
                onExitWithoutSaving={handleExitWithoutSaving}
                isSaving={isSavingBeforeExit}
            />
        </div>
    );
}

function ExperienceBuilderWrapper() {
    const { activationId } = useParams();
    const dispatch = useDispatch();
    const { deleteDefinition } = useExperienceDefinition();

    // Error boundary handlers
    const handleDeleteExperience = useCallback(async () => {
        if (!activationId) return;

        try {
            await deleteDefinition(+activationId);

            // Reset all Redux state
            dispatch(resetExperience());
            dispatch(resetScreens());
            dispatch(clearAllFlows());

            // Reload the page to start fresh with the same URL
            window.location.reload();

            toast.success('Experience deleted successfully');
        } catch (error) {
            console.error('Failed to delete experience:', error);
            toast.error('Failed to delete experience');
        }
    }, [activationId, deleteDefinition, dispatch]);

    const handleRestart = useCallback(() => {
        // Reset all Redux state
        dispatch(resetExperience());
        dispatch(resetScreens());
        dispatch(clearAllFlows());

        // Reload the page to start fresh
        window.location.reload();
    }, [dispatch]);

    return (
        <ExperienceErrorBoundary
            activationId={activationId ? +activationId : 0}
            onDeleteExperience={handleDeleteExperience}
            onRestart={handleRestart}
        >
            <ExperienceBuilderContent />
        </ExperienceErrorBoundary>
    );
}

export default function ExperienceBuilder() {
    return (
        <Provider store={store}>
            <ExperienceBuilderWrapper />
        </Provider>
    );
}

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}
